import { Store } from 'vuex';
import { AxiosResponse, AxiosInstance, AxiosError } from 'axios';
import { ApiService } from '../api/api.service';
import { AppState } from '@/store';
import { Logger } from '../logger';
import { MUTATIONS } from '@/store/leaderboard';
import { AnalyticsService } from '../analytics/analytics.service';
import { GetLeaderboardDataResponse, SummerOfferResponse, SummerRedemptionRequest, SweepstakeRequest, SweepstakeResponse } from '@/models/leaderboard';


export interface LeaderboardService {
  getLeaderboardData(): Promise<any>;
  saveSweepstake(sweepstake: SweepstakeRequest): Promise<SweepstakeResponse>;
  getSummerOffers(month: number): Promise<any>;
  redeemSummerOffer(redemption: SummerRedemptionRequest): Promise<any>;
}

export class AppLeaderboardService extends ApiService implements LeaderboardService {

  constructor(
    store: Store<AppState>,
    httpClient: AxiosInstance,
    logger: Logger,
    protected analytics: AnalyticsService,
  ) {
    super(store, httpClient, logger, analytics);
  }

  async getLeaderboardData() {
        return this.httpClient
          .get(`/loyalty-leaderboard/v1/leaderboard/leaderboarddata`)
          .then((res: AxiosResponse<GetLeaderboardDataResponse>) => {
            if (res.data) {
              // Change month number to a zero-based index for javascript
              // API returns 6 for June, but we need 5 for June
              res.data.summerCampaigns.forEach(x => { x.month = (x.month - 1) });
              res.data.summerCampaigns.sort((a, b) => (a.month! < b.month! ? -1: 1));
              res.data.summerCampaigns.forEach(campaign => {
                campaign.milestones.sort((a, b) => (a.segment < b.segment ? -1 : 1));
              });
            }
            this.store.commit(MUTATIONS.SET_LEADERBOARD_DATA, res.data);
          })
          .catch(e => this.handleError('getLeaderboardData', e));
  }
  
  private handleError(operationName: string, e: AxiosError) {
    const data: GetLeaderboardDataResponse = {
      isInErrorState: true,
      sweepstakesEntries: 0,
      statistics: {
          restaurantsVisited: 0,
          badgesEarned: 0,
          nationalRankPercentage: 0,
          stateRankPercentage: 0,
          stateRanked: '',
          stateCode: '',
          stateImageUri: '',
          team: '',
          favoriteEntree: '',
          favoriteEntreeImageUrl: '',
          restaurantRankPercentage: 0,
          favoriteRestaurant: {
              numberOfOrders: 0,
              name: '', 
              city: '',
              state: '',
              crossStreets: [],
              addressLine1: ''
          }
      },
      summerCampaigns: [],
      sideQuests: [],
      achievements: {
          totalPointsEarned: 0,
          badges: []
      }
    }
    this.store.commit(MUTATIONS.SET_LEADERBOARD_DATA, data);

    return this.logAndThrowApiError(operationName, e);
  }

  async saveSweepstake(sweepstake: SweepstakeRequest): Promise<SweepstakeResponse> {
    return this.httpClient
      .post(`/loyalty-leaderboard/v1/leaderboard/sweepstake`, sweepstake)
      .then((res: AxiosResponse<SweepstakeResponse>) => {
        return res.data;
      })
      .catch(e => this.logAndThrowApiError('saveSweepstake', e));
  }

  async getSummerOffers(month: number): Promise<any> {
    return this.httpClient
      .get(`/loyalty-leaderboard/v1/leaderboard/summeroffers/${month}`)
      .then((res: AxiosResponse<SummerOfferResponse>) => {
        return res.data;
      })
      .catch(e => this.logAndThrowApiError('getSummerOffers', e));
  }

  async redeemSummerOffer(redemption: SummerRedemptionRequest): Promise<any> {
    return this.httpClient
      .post(`/loyalty-leaderboard/v1/leaderboard/redeem`, redemption)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(e => this.logAndThrowApiError('redeemSummerOffer', e));
  }
}
